import {
  ActiveProvidersResponse,
  AuthorizationUrlResponse,
  CreateLoginProviderRequest,
  LinkAccountRequest,
  LinkAccountResponse,
  LinkedAccountsResponse,
  LoginProvider,
  LoginProviderListParams,
  LoginProviderResponse,
  LoginProvidersListResponse,
  SyncWellKnownResponse,
  TestProviderResponse,
} from '@/types/sso.types';
import { getApiBaseUrl, API_ENDPOINTS } from '../config/api.config';
import apiClient from './api';
import axios from 'axios';

class SSOService {
  // Admin Provider Management
  
  /**
   * Create a new login provider
   */
  async createProvider(providerData: CreateLoginProviderRequest): Promise<LoginProvider> {
    const response = await apiClient.post<LoginProviderResponse>(
      API_ENDPOINTS.LOGIN_PROVIDERS.CREATE,
      providerData
    );
    return response.data.data;
  }

  /**
   * Get list of login providers with optional filters
   */
  async getProviders(params: LoginProviderListParams = {}): Promise<{
    providers: LoginProvider[];
    total: number;
    page: number;
    limit: number;
  }> {
    const response = await apiClient.get<LoginProvidersListResponse>(
      API_ENDPOINTS.LOGIN_PROVIDERS.LIST,
      { params }
    );
    return response.data.data;
  }

  /**
   * Get a specific login provider by ID
   */
  async getProvider(id: string): Promise<LoginProvider> {
    const response = await apiClient.get<LoginProviderResponse>(
      API_ENDPOINTS.LOGIN_PROVIDERS.GET_BY_ID(id)
    );
    return response.data.data;
  }

  /**
   * Update a login provider
   */
  async updateProvider(id: string, providerData: Partial<CreateLoginProviderRequest>): Promise<LoginProvider> {
    const response = await apiClient.put<LoginProviderResponse>(
      API_ENDPOINTS.LOGIN_PROVIDERS.UPDATE(id),
      providerData
    );
    return response.data.data;
  }

  /**
   * Delete a login provider
   */
  async deleteProvider(id: string): Promise<void> {
    await apiClient.delete(API_ENDPOINTS.LOGIN_PROVIDERS.DELETE(id));
  }

  /**
   * Test provider configuration
   */
  async testProvider(id: string): Promise<TestProviderResponse['data']> {
    const response = await apiClient.post<TestProviderResponse>(
      API_ENDPOINTS.LOGIN_PROVIDERS.TEST(id)
    );
    return response.data.data;
  }

  /**
   * Sync well-known configuration for a provider
   */
  async syncWellKnown(id: string): Promise<LoginProvider> {
    const response = await apiClient.post<SyncWellKnownResponse>(
      API_ENDPOINTS.LOGIN_PROVIDERS.SYNC_WELLKNOWN(id)
    );
    return response.data.data;
  }

  /**
   * Enable/disable a provider
   */
  async toggleProvider(id: string, enabled: boolean): Promise<LoginProvider> {
    return this.updateProvider(id, { enabled });
  }

  /**
   * Bulk enable/disable providers
   */
  async bulkToggleProviders(providerIds: string[], enabled: boolean): Promise<void> {
    const promises = providerIds.map(id => this.toggleProvider(id, enabled));
    await Promise.all(promises);
  }

  // Public SSO Authentication

  /**
   * Get active SSO providers for login page
   */
  async getActiveProviders(): Promise<ActiveProvidersResponse['data']> {
    const response = await apiClient.get<ActiveProvidersResponse>(API_ENDPOINTS.AUTH.SSO_PROVIDERS);
    return response.data.data;
  }

  /**
   * Generate authorization URL for SSO login
   */
  async getAuthorizationUrl(providerName: string, returnUrl?: string): Promise<AuthorizationUrlResponse['data']> {
    const params = returnUrl ? { return_url: returnUrl } : {};
    const response = await apiClient.get<AuthorizationUrlResponse>(
      API_ENDPOINTS.AUTH.SSO_AUTHORIZE(providerName),
      { params }
    );
    return response.data.data;
  }

  /**
   * Initiate SSO login by redirecting to provider
   */
  async initiateLogin(providerName: string, returnUrl?: string): Promise<void> {
    const { authorization_url } = await this.getAuthorizationUrl(providerName, returnUrl);
    window.location.href = authorization_url;
  }

  // Account Linking Management

  /**
   * Get user's linked social accounts
   */
  async getLinkedAccounts(): Promise<LinkedAccountsResponse['data']['accounts']> {
    const response = await apiClient.get<LinkedAccountsResponse>(API_ENDPOINTS.AUTH.SSO_LINKED_ACCOUNTS);
    return response.data.data.accounts;
  }

  /**
   * Link a new social account to current user
   */
  async linkAccount(linkData: LinkAccountRequest): Promise<LinkAccountResponse['data']> {
    const response = await apiClient.post<LinkAccountResponse>(API_ENDPOINTS.AUTH.SSO_LINK, linkData);
    return response.data.data;
  }

  /**
   * Unlink a social account from current user
   */
  async unlinkAccount(providerId: string): Promise<LinkAccountResponse['data']> {
    const response = await apiClient.delete<LinkAccountResponse>(API_ENDPOINTS.AUTH.SSO_UNLINK(providerId));
    return response.data.data;
  }

  // Helper Methods

  /**
   * Validate well-known URL by fetching configuration
   */
  async validateWellKnownUrl(url: string): Promise<boolean> {
    try {
      const response = await axios.get(url, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'SkillZen-SSO-Validator'
        }
      });
      const config = response.data;
      return !!(config.issuer && config.authorization_endpoint && config.token_endpoint);
    } catch (error) {
      console.error('Failed to validate well-known URL:', error);
      return false;
    }
  }

  /**
   * Generate redirect URI for a provider
   */
  generateRedirectUri(providerName: string): string {
    const apiBaseUrl = getApiBaseUrl();
    return `${apiBaseUrl}/api/auth/sso/${providerName}/callback`;
  }

  /**
   * Get default scope for a provider
   */
  getDefaultScope(providerName: string): string {
    const scopes: Record<string, string> = {
      google: 'openid profile email',
      microsoft: 'openid profile email',
      okta: 'openid profile email',
      github: 'user:email',
      linkedin: 'r_liteprofile r_emailaddress',
      facebook: 'email public_profile',
    };
    return scopes[providerName] || 'openid profile email';
  }

  /**
   * Get provider logo URL
   */
  getProviderLogoUrl(providerName: string): string {
    return `/assets/logos/${providerName}-logo.svg`;
  }

  /**
   * Export providers configuration
   */
  async exportProviders(): Promise<LoginProvider[]> {
    const { providers } = await this.getProviders({ limit: 100 });
    return providers.map(provider => ({
      ...provider,
      client_secret: '••••••••', // Mask sensitive data
    }));
  }

  /**
   * Import providers configuration
   */
  async importProviders(providers: CreateLoginProviderRequest[]): Promise<LoginProvider[]> {
    const results: LoginProvider[] = [];
    
    for (const provider of providers) {
      try {
        const created = await this.createProvider(provider);
        results.push(created);
      } catch (error) {
        console.error(`Failed to import provider ${provider.provider_name}:`, error);
      }
    }
    
    return results;
  }

  // Legacy methods for backward compatibility
  async getSSOProviders() {
    return this.getProviders();
  }

  async getSSOProviderById(id: string) {
    return this.getProvider(id);
  }

  async createSSOProvider(data: CreateLoginProviderRequest) {
    return this.createProvider(data);
  }

  async updateSSOProvider(id: string, data: Partial<CreateLoginProviderRequest>) {
    return this.updateProvider(id, data);
  }

  async deleteSSOProvider(id: string) {
    return this.deleteProvider(id);
  }
}

export const ssoService = new SSOService();
export default ssoService;
